<template>
  <TopBarShell mobile-header class="video-owner-top-bar" data-testid="video-owner-top-bar">
    <template #actions>
      <!-- Mode switcher + thin divider before the icon-button cluster. Not
           rendered on mobile — it renders again under the item title there
           instead (see VideoOwnerLayout.vue), so it doesn't crowd the icon
           buttons in this row. -->
      <template v-if="!isSmallMobile">
        <ViewerModeToggle class="video-owner-top-bar__mode-toggle" />
        <span v-if="hasMultipleModes" class="video-owner-top-bar__divider" />
      </template>

      <button
        class="video-owner-top-bar__icon-btn"
        title="Edit"
        aria-label="Edit"
        :disabled="isEditProcessing"
        data-testid="viewer-topbar-edit"
        @click="!isEditProcessing ? annotateAction() : undefined"
      >
        <Icon name="general.pencil" size="base" />
      </button>

      <!-- Single button for the agent flow:
           - no agent yet  → bolt icon, click kicks off generation
           - agent exists  → external-link icon, click opens the agent view
           - generation in progress → spinning refresh icon, disabled
           Rebuilding an existing agent lives on the agent detail page
           (next to delete), so the top bar stays lean. -->
      <template v-if="agentsEnabled && !isMobile">
        <button
          v-if="isCreatingAgent"
          class="video-owner-top-bar__icon-btn"
          :title="agentButtonLabel"
          :aria-label="agentButtonLabel"
          disabled
          data-testid="viewer-topbar-agent-busy"
        >
          <Icon name="general.refresh" size="base" class="animate-spin" />
        </button>
        <button
          v-else-if="existingAgentId"
          class="video-owner-top-bar__icon-btn"
          title="View Agent"
          aria-label="View Agent"
          data-testid="viewer-topbar-view-agent"
          @click="viewAgent"
        >
          <Icon name="ai.robot" size="base" />
        </button>
        <button
          v-else
          class="video-owner-top-bar__icon-btn"
          title="Create Agent"
          aria-label="Create Agent"
          data-testid="viewer-topbar-create-agent"
          @click="createAgentAction()"
        >
          <Icon name="ai.robot" size="base" />
        </button>
      </template>

      <TopBarActions :mobile-overflow="true" :mobile-menu-items="agentMenuItems" />
      <ViewerAppControls />
    </template>
  </TopBarShell>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { Icon } from '@/storybook';

import TopBarShell from '../../shared/TopBarShell.vue';
import TopBarActions from '../../shared/TopBarActions.vue';
import ViewerAppControls from '../../shared/ViewerAppControls.vue';
import ViewerModeToggle from '@/modules/viewer/components/ViewerModeToggle.vue';

import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useItemActions } from '@/modules/viewer/composables/useItemActions';
import { useAgentActions } from '@/modules/viewer/composables/useAgentActions';
import { useViewerAvailableModes } from '@/modules/viewer/composables/useViewerAvailableModes';
import { useFeatures } from '@/composables/useFeatures';
import { useMobileDetection } from '@/composables/useMobileDetection';

const { isMobile: isSmallMobile } = useMobileDetection();
const { isEditProcessing } = storeToRefs(useViewerItemStore());
const { annotateAction } = useItemActions();
const { isCreatingAgent, existingAgentId, agentButtonLabel, createAgentAction, viewAgent } = useAgentActions();

// Same `foundry-agents` LD flag that gates the controller's create endpoint
// and the dashboard route. Default false so the toolbar stays hidden when
// the flag isn't configured, even if the rest of the rollout is permissive.
const { getFeature } = useFeatures();
const agentsEnabled = getFeature('foundry-agents', false);
const { isMobile } = useMobileDetection();

const agentMenuItems = computed(() => {
  if (!isMobile.value || !agentsEnabled.value) {
    return [];
  }

  if (isCreatingAgent.value) {
    return [
      {
        name: 'agent',
        title: agentButtonLabel.value,
        icon: 'ai.robot',
        type: 'secondary',
        disabled: true,
        loading: true,
        action: createAgentAction,
      },
    ];
  }

  if (existingAgentId.value) {
    return [
      {
        name: 'agent',
        title: 'View Agent',
        icon: 'ai.robot',
        type: 'secondary',
        action: viewAgent,
      },
    ];
  }

  return [
    {
      name: 'agent',
      title: 'Create Agent',
      icon: 'ai.robot',
      type: 'secondary',
      action: createAgentAction,
    },
  ];
});

// Only render the divider when the mode switcher is visible — otherwise a
// stray vertical line floats next to the edit button with no left neighbor.
const { availableModes } = useViewerAvailableModes({ syncCurrentMode: false });
const hasMultipleModes = computed(() => availableModes.value.length > 1);
</script>

<style scoped>
.video-owner-top-bar {
  @apply p-0 sm:pb-2 sm:pt-0;
}

.video-owner-top-bar__divider {
  @apply w-px h-5 bg-zinc-200 dark:bg-zinc-700 mx-1;
}

.video-owner-top-bar__icon-btn {
  @apply p-2 rounded-md text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed;
}

@media (max-width: 639px) {
  .video-owner-top-bar__icon-btn {
    min-width: 40px;
    min-height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
